import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { IconInfoCircle, IconSearch } from '@tabler/icons-react'

import { Toggle } from '@/components/forms/Checkbox'
import Select from '@/components/forms/Select'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Button } from '@/components/buttons/Button'
import { TextInput } from '@/components/forms/TextInput'
import { Menu } from '@/components/navigation/Menu'
import { TreeTable } from '@/components/tables/TreeTable/TreeTable'

import {
  collectLeafCriteriaRows,
  convertCriteriaRowsToTableRows,
  convertPlaybookBodyToCriteriaObjects,
  countSelectedCriteriaRows,
} from '../helpers/index'

export const ScorecardCriteriaButton = ({
  criteria,
  mustBePresent,
  toggleMustBePresent,
  readOnly,
}) => {
  const [mustBePresentState, setMustBePresentState] = useState(mustBePresent)

  useEffect(() => {
    setMustBePresentState(criteria?.must_be_present)
  }, [criteria])

  // Inactive rows render an invisible copy of the button so selecting a row
  // doesn't change its height
  if (!criteria?.active) {
    return <Button xs className="visibility-hidden" content="Must Be Present" />
  }

  const handleClick = () => {
    toggleMustBePresent(criteria)
    setMustBePresentState(!mustBePresentState)
  }

  return (
    <Button
      onClick={handleClick}
      data-testid={mustBePresentState ? 'must_be_present' : 'must_not_be_present'}
      disabled={readOnly}
      xs
      destructive={!mustBePresentState}
      success={mustBePresentState}
      content={mustBePresentState ? 'Must Be Present' : 'Must Not Be Present'}
    />
  )
}

export const ScorecardCriteriaWeightInput = ({ criteria, changeCriteriaWeight, readOnly }) => {
  const [currentWeight, setCurrentWeight] = useState(criteria?.weight || 1)

  useEffect(() => {
    setCurrentWeight(criteria?.weight)
  }, [criteria])

  // Same-size invisible placeholder, see ScorecardCriteriaButton
  if (!criteria?.active) {
    return <TextInput type="number" small placeholder="1" className="visibility-hidden" />
  }

  const handleWeightChange = ({ target: { value } }) => {
    setCurrentWeight(value)
    changeCriteriaWeight({ ...criteria, weight: value })
  }

  return (
    <TextInput
      type="number"
      small
      placeholder="1"
      value={currentWeight}
      disabled={readOnly}
      data-testid="criteria-weight-input"
      max={100}
      min={0}
      onChange={handleWeightChange}
    />
  )
}

export const ScorecardCriteriaSelector = ({
  findInMeasure,
  onClick,
  toggleFail,
  toggleZero,
  toggleCriteriaSettings,
  changeCriteriaWeight,
  measureDraft,
  toggleConditionType,
}) => {
  const [activePlaybookSection, setActivePlaybookSection] = useState('checklist')

  const [filterBy, setFilter] = useState('')
  const { data } = useSelector((state) => state.scorecards)
  const { body } = data.targetPlaybook || {}

  const autoFail = measureDraft?.auto_fail || false
  const autoZero = measureDraft?.auto_zero || false

  const conditionType =
    measureDraft && measureDraft.condition_type ? measureDraft.condition_type : ''

  const menuItems = [
    { label: 'Checklist', accessor: 'checklist' },
    { label: 'Dynamic Prompts', accessor: 'deck' },
    { label: 'Notifications', accessor: 'notifications' },
    { label: 'Post Call', accessor: 'classified_postcall' },
  ]

  const handleMenuChange = (name) => {
    setActivePlaybookSection(name)
  }

  const handleSearch = (event) => {
    setFilter(event.target.value)
  }

  const buildSectionRows = (section) =>
    convertPlaybookBodyToCriteriaObjects(
      body,
      section,
      filterBy,
      findInMeasure,
      toggleCriteriaSettings,
      changeCriteriaWeight
    ) || []

  const currentRows = buildSectionRows(activePlaybookSection)
  const tableRows = convertCriteriaRowsToTableRows(currentRows)
  const leafRows = collectLeafCriteriaRows(currentRows)
  const selectedLeafCount = leafRows.filter((row) => row.selected).length
  const allLeavesSelected = !isEmpty(leafRows) && selectedLeafCount === leafRows.length

  const columns = [
    {
      label: selectedLeafCount ? `${selectedLeafCount} Criteria Selected` : 'Name',
      accessor: 'name',
    },
    { label: 'Present or Not?', accessor: 'must_be_present' },
  ]

  if (conditionType === 'AND') {
    columns.push({ label: 'Weight', accessor: 'weight', headerAlignment: 'right' })
  }

  const getMenuCount = (name) => countSelectedCriteriaRows(buildSectionRows(name))

  // Bulk toggles unwrap {value, as} cells into the plain criteria shape
  // handleCriteriaChange expects (weight defaults to '1'), and skip leaves
  // already in the target state so criteria are never added or removed twice
  const toggleAutomatedLeaves = (leaves, value) => {
    leaves
      .filter((leaf) => !!leaf.selected !== value)
      .forEach((leaf) => {
        onClick(
          {
            ...leaf,
            active: value,
            weight: leaf.weight?.value || '1',
            must_be_present: leaf.must_be_present?.value,
          },
          value
        )
      })
  }

  const handleRowToggle = (row) => {
    const leaves = row.leafRows || [row]
    const value = row.leafRows ? !(row.selected || row.indeterminate) : !row.selected
    toggleAutomatedLeaves(leaves, value)
  }

  const headerCheckbox = {
    checked: allLeavesSelected,
    indeterminate: selectedLeafCount > 0 && !allLeavesSelected,
    onChange: () => toggleAutomatedLeaves(leafRows, selectedLeafCount === 0),
  }

  const conditionTypeOptions = [
    {
      label: 'AND Logic',
      value: 'AND',
      description: 'All selected items will need to be correct in order to receive full credit.',
    },
    {
      label: 'OR Logic',
      value: 'OR',
      description:
        'Only one of the selected items will need to be correct in order for agents to receive full credit.',
    },
  ]

  return (
    <div data-testid="scorecard-criteria">
      <header className="flex-align-center flex-space-between mb">
        <Select
          options={conditionTypeOptions}
          value={conditionType}
          onChange={(option) => toggleConditionType(option.value)}
          placeholder={`${conditionType} Logic`}
          isClearable={false}
          isSearchable={false}
        />
        <div className="flex-align-center gap">
          {conditionType !== 'OR' && (
            <>
              <div className="flex-align-start small-gap">
                <strong>Auto-zero</strong>
                <InteractiveTooltip content="With auto-zero on, if any items are missed on the call, this measure will be zeroed out.">
                  <IconInfoCircle className="icon-svg-large status-informative" />
                </InteractiveTooltip>
                <Toggle
                  aria-label="Auto-zero"
                  checked={autoZero}
                  data-testid="autozero-toggle"
                  onClick={() => toggleZero()}
                />
              </div>
              <div className="vertical-divider" />
            </>
          )}
          <div className="flex-align-start small-gap">
            <strong>Auto-fail</strong>
            <InteractiveTooltip content="With auto-fail on, if any items are missed on the call, this measure will be zeroed out and the call will be marked as a failure.">
              <IconInfoCircle className="icon-svg-large status-informative" />
            </InteractiveTooltip>

            <Toggle
              aria-label="Auto-fail"
              checked={autoFail}
              data-testid="autofail-toggle"
              onClick={() => toggleFail()}
            />
          </div>
        </div>
      </header>

      <Menu
        menuStyle="bar"
        items={menuItems.map((menuItem) => {
          const count = getMenuCount(menuItem.accessor)
          return {
            accessor: menuItem.accessor,
            label: <strong>{menuItem.label}</strong>,
            active: activePlaybookSection === menuItem.accessor,
            onClick: () => handleMenuChange(menuItem.accessor),
            badge: count > 0 ? count : undefined,
          }
        })}
      />
      <div className="flex-align-center gap mt">
        <div className="search-input mb full-width">
          <input
            type="text"
            name="filter"
            placeholder="Search for criteria..."
            value={filterBy}
            onChange={handleSearch}
          />
          <IconSearch />
        </div>
      </div>

      {!isEmpty(currentRows) ? (
        <TreeTable
          key={activePlaybookSection}
          rowKey="key"
          rows={tableRows}
          columns={columns}
          defaultExpandAll
          onRowToggle={handleRowToggle}
          headerCheckbox={headerCheckbox}
        />
      ) : (
        <div className="empty-table" data-testid="empty-list">
          Nothing matches your filter
        </div>
      )}
    </div>
  )
}
